import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Send as SendIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { LessonViewer } from '../shared/LessonViewer';
import {
  getModuleContent,
  getModule,
  updateAssignmentStatus,
  submitAssignment,
  getStudentAssignments,
} from '../../services/lessonService';
import { useAuth } from '../../hooks/useAuth';

export const ModuleViewerWithSubmission: React.FC = () => {
  const navigate = useNavigate();
  const { moduleId } = useParams<{ moduleId: string }>();
  const { user } = useAuth();
  const [module, setModule] = useState<any>(null);
  const [units, setUnits] = useState<any[]>([]);
  const [assignment, setAssignment] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSubmissionDialog, setShowSubmissionDialog] = useState(false);
  const [submissionText, setSubmissionText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [completedUnits, setCompletedUnits] = useState<string[]>([]);

  useEffect(() => {
    if (moduleId && user) {
      loadModuleAndAssignment();
    }
  }, [moduleId, user]);

  const loadModuleAndAssignment = async () => {
    if (!moduleId || !user) return;

    try {
      setLoading(true);
      setError(null);

      // Load module metadata
      const moduleData = await getModule(moduleId);
      setModule(moduleData);

      // Load module content
      const contentData = await getModuleContent(moduleId);
      setUnits(contentData.units || []);

      // Load student's assignment for this module using 'me'
      const assignmentsData = await getStudentAssignments('me');
      const currentAssignment = assignmentsData.assignments.find(
        (a: any) => a.module_id === moduleId,
      );

      if (currentAssignment) {
        setAssignment(currentAssignment);

        // If assignment is 'assigned', update to 'in_progress'
        if (currentAssignment.status === 'assigned') {
          await updateAssignmentStatus(currentAssignment.assignment_id, 'in_progress');
          currentAssignment.status = 'in_progress';
          setAssignment({ ...currentAssignment });
        }
      }
    } catch (err: any) {
      console.error('Error loading module:', err);
      setError(err.message || 'Failed to load module');
    } finally {
      setLoading(false);
    }
  };

  const handleModuleComplete = () => {
    // When student completes viewing all units, show submission dialog
    if (assignment && assignment.status !== 'completed') {
      const unitIds = units.map((u) => u.unit_id);
      setCompletedUnits(unitIds);
      setShowSubmissionDialog(true);
    } else {
      // Already completed, just go back
      navigate('/student/assignments');
    }
  };

  const handleSubmit = async () => {
    if (!assignment || !submissionText.trim()) {
      setError('Please provide a response before submitting');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await submitAssignment(assignment.assignment_id, submissionText, undefined, completedUnits);

      setSubmitSuccess(true);
      setShowSubmissionDialog(false);

      // Reload assignment to get updated status
      setTimeout(() => {
        navigate('/student/assignments');
      }, 2000);
    } catch (err: any) {
      console.error('Error submitting assignment:', err);
      setError(err.message || 'Failed to submit assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExit = () => {
    navigate('/student/assignments');
  };

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !module) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleExit}>
          Back to Assignments
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Success Message */}
      {submitSuccess && (
        <Alert
          severity="success"
          sx={{
            position: 'fixed',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
          }}
        >
          Assignment submitted successfully! Redirecting...
        </Alert>
      )}

      {/* Assignment Status Banner */}
      {assignment && (
        <Box
          sx={{
            bgcolor: assignment.status === 'completed' ? 'success.light' : 'info.light',
            py: 1,
            px: 3,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Chip
            label={assignment.status.replace('_', ' ').toUpperCase()}
            color={assignment.status === 'completed' ? 'success' : 'info'}
            size="small"
          />
          {assignment.due_date && (
            <Typography variant="body2">
              Due: {new Date(assignment.due_date).toLocaleDateString()}
            </Typography>
          )}
        </Box>
      )}

      {/* Lesson Viewer */}
      {module && units.length > 0 && (
        <LessonViewer
          moduleTitle={module.title}
          units={units}
          onComplete={handleModuleComplete}
          onExit={handleExit}
          isTeacherPreview={false}
          moduleId={moduleId}
        />
      )}

      {/* Submission Dialog */}
      <Dialog
        open={showSubmissionDialog}
        onClose={() => !submitting && setShowSubmissionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckIcon color="success" />
            <Typography variant="h6">Submit Assignment</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" paragraph>
              Congratulations on completing all units! Please provide your response or reflection on
              what you learned.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              multiline
              rows={8}
              label="Your Response"
              placeholder="Share what you learned, answer any questions, or provide your thoughts on the module..."
              value={submissionText}
              onChange={(e) => setSubmissionText(e.target.value)}
              disabled={submitting}
              required
              helperText={`${submissionText.length} characters`}
            />

            {assignment?.instructions && (
              <Card sx={{ mt: 2, bgcolor: 'info.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Assignment Instructions:
                  </Typography>
                  <Typography variant="body2">{assignment.instructions}</Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowSubmissionDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !submissionText.trim()}
            startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {submitting ? 'Submitting...' : 'Submit Assignment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModuleViewerWithSubmission;
